import { asNumber, asRecord, asString } from "@/data/mapper/common/mapper-utils";
import { OperationLog, PagedLogs } from "@/domain/entity/admin/operation-log-entity";
import { translate } from "@/utils/i18n";

export class OperationLogMapper {
  static toEntity(dto: unknown): PagedLogs {
    const map = asRecord(dto);
    const rows = Array.isArray(map.items)
      ? map.items
      : Array.isArray(map.list)
        ? map.list
        : Array.isArray(map.data)
          ? map.data
          : [];
    const pageSize = asNumber(map.pageSize);
    const totalCount = asNumber(map.total ?? map.totalCount);
    return {
      totalPage: asNumber(map.totalPage ?? (pageSize > 0 ? Math.ceil(totalCount / pageSize) : 0)),
      totalCount,
      data: rows.map((item) => mapOperationLogItem(item)),
    };
  }
}

function mapOperationLogItem(item: unknown): OperationLog {
  const map = asRecord(item);
  const userRole = asString(map.userRole ?? map.role ?? map.type);
  const userId = asString(map.userId ?? map.userID ?? map.id);
  const event = asString(map.event ?? map.action);
  const content = map.content ?? map.prop ?? map.detail;
  const existingDetail =
    typeof map.detail === "string" || typeof map.detail === "number"
      ? asString(map.detail)
      : "";

  return {
    ...map,
    uuid: asString(map.uuid ?? map.logUuid ?? map.operationLogUuid) || undefined,
    createdAt: asString(map.createdAt ?? map.occurredAt),
    actor: asString(map.actor) || formatUserRole(userRole),
    target: asString(map.target) || userId,
    action: asString(map.action) || event,
    detail: existingDetail || formatLogContent(content),
  };
}

function formatUserRole(role: string): string {
  switch (role) {
    case "SuperAdmin":
    case "Admin":
      return translate("role.admin");
    case "Doctor":
      return translate("role.medicalExpert");
    case "Nurse":
      return translate("role.nurse");
    case "Patient":
      return translate("role.patient");
    case "Guardian":
      return translate("role.guardian");
    case "Cro":
    case "CRO":
      return "CRO";
    default:
      return role;
  }
}

function formatLogContent(value: unknown): string {
  if (value == null || value === "") return "-";
  if (typeof value === "string") return formatStringContent(value);
  if (typeof value !== "object") return String(value);

  const entries = Object.entries(asRecord(value))
    .filter(([, entryValue]) => entryValue !== undefined && entryValue !== null && entryValue !== "")
    .map(([key, entryValue]) => `${key}: ${formatLogValue(entryValue)}`);

  return entries.length > 0 ? entries.join(", ") : "-";
}

function formatStringContent(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "-";
  try {
    return formatLogContent(JSON.parse(trimmed));
  } catch {
    return trimmed;
  }
}

function formatLogValue(value: unknown): string {
  if (value == null) return "-";
  if (Array.isArray(value)) return `[${value.map(formatLogValue).join(", ")}]`;
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}
